import { queryOptions } from '@tanstack/react-query';

import { EQueryKeys } from '@enums/queryKeys.enum';
import { medicationImportFileSchema } from '@schemas/medicationImportFile.schema';
import { medicationCreatesService } from '@services/medicationCreates/service';
import type { GetMedicationCreateByIdOutput } from '@services/medicationCreates/types/getById.type';

import type { ContentEditorSettings } from './types';

export const medicationCreateEditorSettings = {
  api: {
    createMessage: ({ demandId, message }) =>
      medicationCreatesService.createMessage({
        medicationCreateId: demandId,
        message,
      }),
    createTopic: ({ content, demandId, position, title }) =>
      medicationCreatesService.createTopic({
        content,
        medicationCreateId: demandId,
        position,
        title,
      }),
    importTopics: ({ demandId, file }) =>
      medicationCreatesService.importTopics({
        file,
        medicationCreateId: demandId,
      }),
    markReview: demandId => medicationCreatesService.markReview(demandId),
    removeAllTopics: demandId =>
      medicationCreatesService.removeAllTopics({
        medicationCreateId: demandId,
      }),
    removeTopic: ({ demandId, id }) =>
      medicationCreatesService.removeTopic({
        id,
        medicationCreateId: demandId,
      }),
    reorderTopics: ({ demandId, ids }) =>
      medicationCreatesService.reorderTopics({
        ids,
        medicationCreateId: demandId,
      }),
    updateMessage: ({ demandId, message, messageId }) =>
      medicationCreatesService.updateMessage({
        medicationCreateId: demandId,
        medicationCreateMessageId: messageId,
        message,
      }),
    updateTopic: ({ content, demandId, id, title }) =>
      medicationCreatesService.updateTopic({
        content,
        id,
        medicationCreateId: demandId,
        title,
      }),
  },
  bulas: {
    create: ({ demandId, ...params }) =>
      medicationCreatesService.createBula({
        ...params,
        medicationCreateId: demandId,
      }),
    remove: ({ demandId, id }) =>
      medicationCreatesService.removeBula({
        id,
        medicationCreateId: demandId,
      }),
    reorder: ({ demandId, ids }) =>
      medicationCreatesService.reorderBulas({
        ids,
        medicationCreateId: demandId,
      }),
    update: ({ demandId, ...params }) =>
      medicationCreatesService.updateBula({
        ...params,
        medicationCreateId: demandId,
      }),
    updateFile: ({ demandId, file, id }) =>
      medicationCreatesService.updateBulaFile({
        file,
        id,
        medicationCreateId: demandId,
      }),
  },
  detailsQueryOptions: (id: string) =>
    queryOptions({
      queryKey: [EQueryKeys.NewMedicationDetails, id],
      queryFn: () => medicationCreatesService.getById(id),
      select: data => data.medication,
      staleTime: 1000 * 60,
      refetchOnWindowFocus: false,
    }),
  import: {
    confirmationNote:
      'Bulas com o mesmo número de referência serão substituídas.',
    fileSchema: medicationImportFileSchema,
  },
  labels: {
    noun: 'medicamento',
    title: 'Detalhes do medicamento',
  },
  listPath: '/my-medications',
  page: '/new-medications',
  refreshDetails: queryClient =>
    queryClient.refetchQueries({
      queryKey: [EQueryKeys.NewMedicationDetails],
    }),
  reviewQueryKeys: [
    [EQueryKeys.NewMedicationDetails],
    [EQueryKeys.MyMedicationUpdates],
    [EQueryKeys.MyMedicationCreates],
    [EQueryKeys.NewMedications],
  ],
} satisfies ContentEditorSettings<GetMedicationCreateByIdOutput>;
